import { Run } from '@/types/runs.ts';
import { ActiveStatuses, ExecutionStatus } from '@/types/statuses.ts';
import {
  browserNotificationsAreAvailable,
  getRunBrowserNotificationsEnabled,
} from '@/hooks/browserNotificationPreference.ts';
import { NavigateFunction } from 'react-router-dom';

export const NOTIFIABLE_TERMINAL_STATUSES = [
  ExecutionStatus.AskUser,
  ExecutionStatus.Completed,
  ExecutionStatus.Errors,
  ExecutionStatus.PartiallyCompleted,
];

export function getRunBrowserNotificationText(run: Run): { title: string; body: string } | null {
  if (run.status === ExecutionStatus.AskUser) {
    return {
      title: 'Run needs attention',
      body: `${run.name} needs input before it can continue.`,
    };
  }

  if (run.status === ExecutionStatus.Completed) {
    return {
      title: 'Run completed',
      body: `${run.name} finished successfully.`,
    };
  }

  if (run.status === ExecutionStatus.PartiallyCompleted) {
    return {
      title: 'Run completed with errors',
      body: `${run.name} finished with some errors.`,
    };
  }

  if (run.status === ExecutionStatus.Errors) {
    return {
      title: 'Run errored',
      body: `${run.name} finished with errors.`,
    };
  }

  return null;
}

export function didRunReachNotifiableStatus(
  previousStatus: ExecutionStatus | undefined,
  nextStatus: ExecutionStatus,
) {
  return (
    previousStatus !== undefined &&
    ActiveStatuses.includes(previousStatus) &&
    NOTIFIABLE_TERMINAL_STATUSES.includes(nextStatus)
  );
}

export function showRunBrowserNotification({
  automationId,
  run,
  navigate,
}: {
  automationId: string;
  run: Run;
  navigate: NavigateFunction;
}) {
  const notificationText = getRunBrowserNotificationText(run);
  if (!notificationText) return;

  if (
    !getRunBrowserNotificationsEnabled() ||
    !browserNotificationsAreAvailable() ||
    Notification.permission !== 'granted'
  ) {
    return;
  }

  const notificationKey = `${run.id}:${run.status}`;
  const notification = new Notification(notificationText.title, {
    body: notificationText.body,
    tag: notificationKey,
  });

  notification.onclick = () => {
    window.focus();
    navigate(`/automations/${automationId}/runs/${run.id}`);
    notification.close();
  };
}
